import { datadogRum } from '@datadog/browser-rum';
import Cookies from 'js-cookie';
import React, {
  createContext, PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import APIGateway from '@/api';
import { Loader } from '@/components/loader/loader';
import { useCapabilities } from '@/hooks/queries/useFga';
import {
  AuthContextVal, AuthTokenExchangeArgs, LoginRedirectArgs, SessionContext
} from '@/types/auth';
import { SESSION_COOKIE,SessionCookie } from '@/types/cookie';
import { envVarsProvider } from '@/utils/envVars/envVars';

export const AuthContext = createContext( {} as AuthContextVal );

interface WrapperProps {
  children: ReactNode;
}

export default function AuthWrapper( { children }: PropsWithChildren<WrapperProps> ) {
  const navigate = useNavigate();

  const sessionCookie = getCookieVals();

  const [ authAudience, setAuthAudience ] = useState<string>();
  const [ authClientDomain, setAuthClientDomain ] = useState<string>();
  const [ authClientId, setAuthClientId ] = useState<string>();
  const [ authOrgId, setAuthOrgId ] = useState<string>();
  const [ studioBasePath, setStudioBasePath ] = useState<string>();
  const [ isLoading, setIsLoading ] = useState<boolean>( true );
  const [ isReady, setIsReady ] = useState<boolean>( false );
  const [ sessionContext, setSessionContext ] = useState<SessionContext | undefined>( sessionCookie );

  useEffect( () => {
    setAuthAudience( envVarsProvider.get( 'AUTH_AUDIENCE' ) );
    setAuthClientDomain( envVarsProvider.get( 'AUTH_CLIENT_DOMAIN' ) );
    setAuthClientId( envVarsProvider.get( 'AUTH_CLIENT_ID' ) );
    setAuthOrgId( envVarsProvider.get( 'AUTH_ORG_ID' ) );
    setStudioBasePath( envVarsProvider.get( 'STUDIO_BASE_PATH' ) );

    if ( sessionCookie?.userId ) {
      datadogRum.setUser( { id: sessionCookie.userId } );
    }

    setIsReady( true );
    setIsLoading( false );
  }, [] );

  function getCookieVals(): SessionCookie | undefined {
    const sessionCookieStr = Cookies.get( SESSION_COOKIE )?.valueOf();

    let sessionCookie: SessionCookie | undefined;

    if ( sessionCookieStr ) {
      sessionCookie = JSON.parse( sessionCookieStr );
    }

    return sessionCookie;
  }

  const loginRedirect = useCallback( async( args: LoginRedirectArgs ): Promise<void> => {
    if( !authClientDomain || !authClientId ) {
      throw new Error( 'Auth configuration is missing' );
    }

    const {
      invitation
    } = args;

    // Generate PKCE challenge and other security params
    const state = crypto.randomUUID();
    const nonce = crypto.randomUUID();

    // Build auth URI
    const origin = window.location.origin;
    const baseUrl = origin + ( studioBasePath === '/' ? '' : studioBasePath );

    const authSearchParams = new URLSearchParams( {
      audience: authAudience || '',
      client_id: authClientId || '',
      nonce,
      organization: authOrgId || '',
      prompt: 'login', // Force login screen, disable SSO
      redirect_uri: baseUrl + '/login/callback',
      response_type: 'code',
      scope: 'email offline_access openid profile',
      state
    } );

    // If invitation, add additional invitation params
    if( invitation ) {
      authSearchParams.append( 'invitation', invitation );
    }

    // Store security params in cookies for verification
    const securityParamCookieOpts: Cookies.CookieAttributes = {
      domain: window.location.hostname,
      expires: new Date( Date.now() + 5 * 60 * 1000 ), // 5 minutes
      path: '/',
      sameSite: 'strict',
      // sameSite: 'lax',
      secure: true
    };

    Cookies.set( 'auth_nonce', nonce, securityParamCookieOpts );
    Cookies.set( 'auth_origin', origin, securityParamCookieOpts );
    Cookies.set( 'auth_state', state, securityParamCookieOpts );

    // Redirect to Auth0
    const authUrl = `https://${ authClientDomain }/authorize?${ authSearchParams }`;

    window.location.replace( authUrl );
  }, [ authClientDomain, authClientId, authAudience, authOrgId ] );

  const acceptInvitation = useCallback( async( invitation: string ): Promise<void> => {
    return await loginRedirect( { invitation } );
  }, [ loginRedirect ] );

  const tokenExchange = useCallback( async( args: AuthTokenExchangeArgs ): Promise<void> => {
    const {
      code,
      state
    } = args;

    // Get temporary auth cookies
    const savedNonce = Cookies.get( 'auth_nonce' );
    const savedOrigin = Cookies.get( 'auth_origin' );
    const savedState = Cookies.get( 'auth_state' );

    // check origin store and match
    if( !savedNonce ) {
      throw Error( 'missing nonce' );
    }

    if( !savedOrigin ) {
      throw Error( 'missing orgin' );
    }

    if( savedOrigin && ( savedOrigin !== window.location.origin ) ) {
      throw Error( 'origin doesn\'t match' );
    }

    // CSRF protection - validate state
    if( savedState !== state ) {
      throw Error( 'state mismatch' );
    }

    const loginCallbackRes = await APIGateway.auth.authTokenExchange( {
      code,
      nonce: savedNonce
    } );

    const {
      sessionContext,
      sessionId
    } = loginCallbackRes;

    if ( sessionContext && sessionId ) {
      const roles = ( sessionContext.roles || [] ).map( role => role.name );

      const nextSessionCookie: SessionCookie = {
        expiresAt: sessionContext.expiresAt,
        issuedAt: sessionContext.issuedAt,
        roles,
        token: sessionId,
        userId: sessionContext.userId
      };

      // Set the session cookie
      Cookies.set( SESSION_COOKIE, JSON.stringify( nextSessionCookie ), {
        domain: window.location.hostname,
        path: '/',
        sameSite: 'strict',
        // sameSite: 'lax',
        secure: true
      } );

      // Clean up temporary auth cookies
      Cookies.remove( 'auth_nonce', { path: '/', domain: window.location.hostname } );
      Cookies.remove( 'auth_origin', { path: '/', domain: window.location.hostname } );
      Cookies.remove( 'auth_state', { path: '/', domain: window.location.hostname } );

      console.log( 'login successful', nextSessionCookie );

      datadogRum.setUser( { id: sessionContext.userId } );

      setSessionContext( nextSessionCookie );

      // Redirect to home
      navigate( '/', { replace: true } );
    } else {
      throw new Error( 'exchange failed' );
    }
  }, [ navigate, setSessionContext ] );

  const logout = useCallback( async(): Promise<void> => {
    if( !sessionContext ) {
      console.error( 'You are not logged in. Unable to logout.' );
      return;
    }

    console.log( 'logging out...' );

    // Step 1: Call your API to revoke refresh token and clear Redis
    try {
      const authLogoutRes = await APIGateway.auth.authLogout();

      if( !authLogoutRes.loggedOut ) {
        throw new Error( 'unable to logout' );
      }
    } catch( error ) {
      console.error( 'Logout error:', error );
    }

    datadogRum.clearUser();

    // Step 2: Clear client-side session/cookies
    Cookies.remove( SESSION_COOKIE, {
      domain: window.location.hostname,
      path: '/'
    } );

    setSessionContext( undefined );

    // Step 3: Redirect to Auth0 logout to clear SSO session
    const origin = window.location.origin;
    const baseUrl = origin + ( studioBasePath === '/' ? '' : studioBasePath );

    const authSearchParams = new URLSearchParams( {
      client_id: authClientId || '',
      returnTo: baseUrl
    } );

    const authUrl = `https://${ authClientDomain }/v2/logout?${ authSearchParams }`;

    window.location.replace( authUrl );
  }, [ sessionContext, authClientId, authClientDomain, setSessionContext ] );

  const { data: capabilities = [] } = useCapabilities( !!sessionContext?.token );

  const canAccess = useCallback( ( capability: string ): boolean => {
    return capabilities.includes( capability );
  }, [ capabilities ] );

  const authContextVal = useMemo( (): AuthContextVal => ( {
    acceptInvitation,
    authAudience,
    authClientDomain,
    authClientId,
    authOrgId,
    canAccess,
    isReady,
    loginRedirect,
    logout,
    sessionContext,
    tokenExchange
  } ), [
    acceptInvitation,
    authAudience,
    authClientDomain,
    authClientId,
    authOrgId,
    canAccess,
    isReady,
    loginRedirect,
    logout,
    sessionContext,
    tokenExchange
  ] );

  return (
    <AuthContext.Provider value={ authContextVal }>
      {
        isLoading
          ? <Loader />
          : children
      }
    </AuthContext.Provider>
  );
}
